import styled from "@emotion/styled";
import {
  Box,
  CircularProgress,
  Fade,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid";
import { AxiosResponse } from "axios";
import { concat } from "lodash";
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import { useLocale } from "../../providers/LocaleProvider";
import { Button } from "../../components/Button";
import AnimatedFooter from "../../components/dialogs/AnimatedFooter";
import { Snackbar } from "../../components/Snackbar";
import { StandardLibraryTable } from "../../components/table/StandardLibraryTable";
import { TextField } from "../../components/TextField";
import { useStandardsLibrary } from "../../contexts/StandardsLibraryContext";
import { useAppMutation } from "../../hooks/useAppMutation";
import { useAppQueryPost } from "../../hooks/useAppQuery";
import {
  ICompetencyImportResponse,
  ICompetencyListResponse,
  IListCompetency
} from "../../interfaces/Competency";
import DownloadIcon from "../../resources/images/icons-download.svg";
import FilterIcon from "../../resources/images/icons-filter.svg";
import RemoveIcons from "../../resources/images/icons-remove.svg";
import SearchIcon from "../../resources/images/icons-search.svg";
import CompetenciesChipFilter from "./CompetenciesChipFilter";
import ImportCompetenciesDialog from "./ImportCompetenciesDialog";
import ImportCompeteniesErrorDialog from "./ImportCompeteniesErrorDialog";
import LimitedOverlay from "./LimitedOverlay";
import {
  AttributeCategory,
  SettingStandardsLibaryFilterCompetencies,
  YES_NO_OPTIONS
} from "./SettingStandardsLibaryFilterCompetencies";
import { SetttingStandardsLibaryListingModal } from "./SetttingStandardsLibaryListingModal";
interface FilterRef {
  categories: AttributeCategory[];
  expiry: string | boolean | null;
  training: string | boolean | null;
}

type State = {
  selectedRows: GridRowSelectionModel;
  showImportCompetencies: boolean;
  showMessage: boolean;
  showFilter: boolean;
  actionTarget: null | HTMLElement;
  searchText: string;
  queryText: string | null;
  selectedCompetency: string | null;
  currentRow: IListCompetency | null;
  showImportCompetenciesError: boolean;
};

type Action =
  | { type: "SET_SELECTED_ROWS"; payload: GridRowSelectionModel }
  | { type: "SET_IMPORT_DIALOG"; payload: boolean }
  | { type: "SET_MESSAGE"; payload: boolean }
  | { type: "SET_FILTER"; payload: boolean }
  | {
      type: "SET_ACTION_TARGET";
      payload: { element: null | HTMLElement; row: IListCompetency | null };
    }
  | { type: "SET_SEARCH_TEXT"; payload: string }
  | { type: "SET_QUERY_TEXT"; payload: string | null }
  | { type: "SET_SELECTED_COMPETENCY"; payload: string | null }
  | { type: "CLEAR_SELECTION" }
  | { type: "SET_IMPORT_COMPETENCIES_ERROR"; payload: boolean };

const initialState: State = {
  selectedRows: [],
  showImportCompetencies: false,
  showImportCompetenciesError: false,
  showMessage: false,
  showFilter: false,
  actionTarget: null,
  searchText: "",
  queryText: null,
  selectedCompetency: null,
  currentRow: null
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_SELECTED_ROWS":
      return { ...state, selectedRows: action.payload };
    case "SET_IMPORT_DIALOG":
      return { ...state, showImportCompetencies: action.payload };
    case "SET_MESSAGE":
      return { ...state, showMessage: action.payload };
    case "SET_FILTER":
      return { ...state, showFilter: action.payload };
    case "SET_ACTION_TARGET":
      return { ...state, actionTarget: action.payload.element, currentRow: action.payload.row };
    case "SET_SEARCH_TEXT":
      return { ...state, searchText: action.payload };
    case "SET_QUERY_TEXT":
      return { ...state, queryText: action.payload };
    case "SET_SELECTED_COMPETENCY":
      return { ...state, selectedCompetency: action.payload };
    case "CLEAR_SELECTION":
      return { ...state, selectedRows: [] };
    case "SET_IMPORT_COMPETENCIES_ERROR":
      return { ...state, showImportCompetenciesError: action.payload };
    default:
      return state;
  }
}

const SettingStandardsLibraryCompetencies = ({ theme }: { theme: any }) => {
  const { translate } = useLocale();
  const { competencies, setRouteState, debounceSearch, isTrial } = useStandardsLibrary();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    selectedRows,
    showImportCompetencies,
    showMessage,
    showFilter,
    actionTarget,
    selectedCompetency,
    currentRow,
    showImportCompetenciesError
  } = state;
  const [cacheRows, setCacheRows] = useState<ICompetencyImportResponse>({
    importedCompetencies: [],
    notImportedCompetencies: [],
    alreadyExistsCompetencies: []
  });
  const pagingModel = competencies.pagingModel;
  const sortModel = competencies.sortModel;

  const importCompetenciesMutation = useAppMutation<
    AxiosResponse<ICompetencyImportResponse>,
    unknown,
    string[]
  >("post", "/StandardLibrary/competencies/import", {
    onSuccess: (data) => {
      dispatch({ type: "SET_MESSAGE", payload: true });
      setCacheRows(data.data);
      dispatch({ type: "SET_SELECTED_ROWS", payload: [] });
      onCloseImportTask();
    },
    onError: () => {}
  });

  const importCompetenciesMutationSingle = useAppMutation<
    AxiosResponse<ICompetencyImportResponse>,
    unknown,
    string[]
  >("post", "/StandardLibrary/competencies/import", {
    onSuccess: (data) => {
      dispatch({ type: "SET_MESSAGE", payload: true });
      setCacheRows(data.data);
      onCloseImportTask();
    },
    onError: () => {}
  });

  const {
    data: listCompetenciesData,
    isLoading: isLoadingCompetency,
    refetch
  } = useAppQueryPost<ICompetencyListResponse>(`/StandardLibrary/competencies`, {
    config: {
      data: competencies.filterRef.categories.map((c) => c.id),
      params: {
        pageNumber: pagingModel.page + 1,
        pageSize: pagingModel.pageSize,
        orderBy: sortModel.length > 0 ? `${sortModel[0].field} ${sortModel[0].sort}` : undefined,
        searchText: (competencies.queryText || "").trim(),
        ...(competencies.filterRef?.expiry != null && { hasExpiry: competencies.filterRef.expiry }),
        ...(competencies.filterRef?.training != null && {
          hasTraining: competencies.filterRef.training
        }),
        showExtra: true
      }
    }
  });

  const isLimitedAccess = useMemo(() => {
    if (isTrial && listCompetenciesData?.competencies?.data) {
      const competencies = listCompetenciesData.competencies?.data;
      return competencies.some((c) => c.isExtra);
    }
    return false;
  }, [listCompetenciesData, isTrial]);

  const rows = useMemo(() => {
    if (isLoadingCompetency || !listCompetenciesData?.competencies?.data) {
      return [];
    }

    return listCompetenciesData.competencies.data;
  }, [listCompetenciesData, isLoadingCompetency]);

  const rowCount = useMemo(
    () => listCompetenciesData?.competencies?.totalCount || null,
    [listCompetenciesData?.competencies?.totalCount]
  );

  const extraCompetencies = useMemo(() => rows.filter((c) => c.isExtra), [rows]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const value = event.target.value;
    debounceSearch("competencies", value);
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    setRouteState("competencies", { filters: newFilters });
  };

  const onImportCompetencies = useCallback(() => {
    if (selectedRows.length > 0) {
      importCompetenciesMutation.mutate(selectedRows as string[]);
    }
  }, [selectedRows, importCompetenciesMutation]);

  const handleOpenFilterModal = useCallback(() => {
    dispatch({ type: "SET_FILTER", payload: true });
  }, []);

  const onCloseFilter = useCallback(() => {
    dispatch({ type: "SET_FILTER", payload: false });
  }, []);

  const onSaveFilter = useCallback(
    (filters: FilterRef) => {
      setRouteState("competencies", { filterRef: filters });
      refetch();
      dispatch({ type: "SET_FILTER", payload: false });
      setRouteState("competencies", { pagingModel: { ...pagingModel, page: 0 } });
    },
    [refetch, pagingModel, setRouteState]
  );

  const onCloseActionMenu = useCallback(() => {
    dispatch({ type: "SET_ACTION_TARGET", payload: { element: null, row: null } });
  }, []);

  const onHandlerImportCompetencies = () => {
    if (!currentRow?.id) return;
    importCompetenciesMutationSingle.mutate([currentRow?.id]);
    onCloseActionMenu();
  };

  const handleActionMenu = useCallback(
    (event: any, row: any) => {
      dispatch({ type: "SET_ACTION_TARGET", payload: { element: event.currentTarget, row } });
    },
    [isTrial]
  );

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      setRouteState("competencies", {
        pagingModel:
          newModel.pageSize !== pagingModel.pageSize
            ? { page: 0, pageSize: newModel.pageSize }
            : newModel
      });
    },
    [pagingModel.pageSize, setRouteState]
  );

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      setRouteState("competencies", { sortModel: newSortModel });
      setRouteState("competencies", { pagingModel: { ...pagingModel, page: 0 } });
    },
    [pagingModel, setRouteState]
  );

  const onClear = useCallback(() => {
    dispatch({ type: "CLEAR_SELECTION" });
  }, []);

  const onCloseImportTask = useCallback(() => {
    dispatch({ type: "SET_IMPORT_DIALOG", payload: false });
  }, []);

  const setStateOfMessage = useCallback((state: boolean) => {
    dispatch({ type: "SET_MESSAGE", payload: state });
  }, []);

  const onImportSingleCompetencies = useCallback((event: unknown, row: any) => {
    dispatch({ type: "SET_SELECTED_COMPETENCY", payload: row.id });
    dispatch({ type: "SET_IMPORT_DIALOG", payload: true });
  }, []);

  const onCloseImportCompetenciesError = useCallback(() => {
    dispatch({ type: "SET_IMPORT_COMPETENCIES_ERROR", payload: false });
  }, []);

  const onImportCompetenciesError = useCallback(() => {
    dispatch({ type: "SET_IMPORT_COMPETENCIES_ERROR", payload: true });
    setStateOfMessage(false);
  }, []);

  const onImportSingleCompetency = useCallback(
    (competencyId: string) => {
      importCompetenciesMutationSingle.mutate([competencyId]);
    },
    [importCompetenciesMutation]
  );

  const [showStandardsLibraryModalTasks, setShowStandardsLibraryModalTasks] = useState(false);
  const ref = useRef<{ id: string; name: string }>({ id: "", name: "" });

  const onClickChipCompetencyTasks = useCallback((event: SyntheticEvent, row: any) => {
    event.stopPropagation();
    event.preventDefault();
    ref.current = {
      id: row.id,
      name: row.name
    };
    setShowStandardsLibraryModalTasks(true);
  }, []);

  const onCloseCompetenciesListingTasks = useCallback(() => {
    setShowStandardsLibraryModalTasks(false);
  }, []);

  const extractorTasks = useCallback((data: any) => {
    return data?.tasks || [];
  }, []);

  const isRowSelectable = useCallback((data: any) => {
    return !data.row.isExtra;
  }, []);

  const [showStandardsLibraryModalJobs, setShowStandardsLibraryModalJobs] = useState(false);

  const onClickChipCompetencyJobs = useCallback((event: SyntheticEvent, row: any) => {
    event.stopPropagation();
    event.preventDefault();
    ref.current = {
      id: row.id,
      name: row.name
    };
    setShowStandardsLibraryModalJobs(true);
  }, []);

  const onCloseCompetenciesListingJobs = useCallback(() => {
    setShowStandardsLibraryModalJobs(false);
  }, []);

  const onRemoveFilter = useCallback(
    (key: string, value: any) => {
      setRouteState("competencies", { filterRef: { ...competencies.filterRef, [key]: value } });
      refetch();
    },
    [competencies.filterRef, refetch, setRouteState]
  );

  const onRemoveAllFilter = useCallback(() => {
    setRouteState("competencies", { filterRef: { categories: [], expiry: null, training: null } });
    refetch();
  }, [refetch, setRouteState]);

  const isVisible = selectedRows.length > 0;

  const handleViewDetail = useCallback(() => {
    if (currentRow) {
      onImportSingleCompetencies(null, currentRow);
    }
    onCloseActionMenu();
  }, [currentRow, onImportSingleCompetencies, onCloseActionMenu]);

  const extractorJobs = useCallback((data: any) => {
    return data?.jobDefinitions || [];
  }, []);

  const [element, setElement] = useState<Element | null>(null);

  useEffect(() => {
    // Query the element after the DataGrid has mounted
    const el = document.querySelector("div.MuiDataGrid-main");
    if (el) {
      setElement(el);
    }
  }, [isLoadingCompetency, rows]);

  const errorCompetencies = useMemo(
    () => concat(cacheRows.alreadyExistsCompetencies, cacheRows.notImportedCompetencies),
    [cacheRows.alreadyExistsCompetencies, cacheRows.notImportedCompetencies]
  );

  return (
    <React.Fragment>
      <Stack
        direction='column'
        spacing={4}
        sx={{
          ".MuiDataGrid-columnHeader:focus-within": {
            outline: "none !important"
          },
          ".MuiDataGrid-cell:focus-within": {
            outline: "none !important"
          },
          ".MuiDataGrid-columnHeader:focus-visible": {
            outline: "none !important"
          }
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
          <TextField
            dense={true}
            placeholder={translate("placeholders.searchCompetency")}
            autoComplete={false}
            leadingIcon={<SearchIcon />}
            fullWidth={true}
            value={competencies.searchText || ""}
            onChange={handleSearchChange}
            sx={{ maxWidth: { md: "300px" }, width: "300px" }}
          />

          <Button
            className='klayo-button_label'
            label={translate("common.filters")}
            theme={theme}
            size='md'
            startIcon={<FilterIcon />}
            sx={{ width: "180px" }}
            onClick={handleOpenFilterModal}
          />
        </Stack>

        <Stack className='klaro-chiparray' direction='row' spacing={1} sx={{ flexWrap: "wrap" }}>
          <CompetenciesChipFilter
            onRemoveAll={onRemoveAllFilter}
            onRemove={onRemoveFilter}
            filterRef={competencies.filterRef}
          />
        </Stack>
        <StyledStandardLibraryTable>
          <StandardLibraryTable<IListCompetency>
            theme={theme}
            translate={translate}
            columns={[
              { type: StandardLibraryTable.columns.name, label: translate("entities.competency") },
              {
                type: StandardLibraryTable.columns.category,
                field: "competencyCategories",
                label: translate("tableColumns.category")
              },
              {
                type: StandardLibraryTable.columns.expiry,
                label: translate("tableColumns.expiry")
              },
              {
                type: StandardLibraryTable.columns.tasks,
                label: translate("tableColumns.tasks"),
                enableChip: true,
                onClick: onClickChipCompetencyTasks
              },
              {
                type: StandardLibraryTable.columns.jobs,
                label: translate("tableColumns.jobs"),
                enableChip: true,
                onClick: onClickChipCompetencyJobs
              },
              {
                type: StandardLibraryTable.columns.traningsIcon,
                label: translate("tableColumns.training")
              },
              { type: StandardLibraryTable.columns.actions }
            ]}
            isRowSelectable={isRowSelectable}
            paginationModel={pagingModel}
            onPaginationModelChange={handlePaginationModelChange}
            paginationMode='server'
            rows={isLoadingCompetency ? [] : rows}
            totalCount={rowCount || 0}
            isLoading={isLoadingCompetency}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sortingMode='server'
            filterMode='server'
            dense={true}
            paper={false}
            checkboxSelection
            onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => {
              dispatch({ type: "SET_SELECTED_ROWS", payload: rowSelectionModel });
            }}
            onJobSelect={onImportSingleCompetencies}
            rowSelectionModel={selectedRows}
            selectable={false}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer !important" } }}
            onActionMenu={handleActionMenu}
            keyword={competencies.searchText}
          />
        </StyledStandardLibraryTable>
      </Stack>

      <Menu
        open={actionTarget !== null}
        anchorEl={actionTarget}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={onCloseActionMenu}
      >
        <MenuItem onClick={handleViewDetail}>
          <ListItemText>{translate("common.viewDetails")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={onHandlerImportCompetencies}>
          <ListItemText>{translate("common.import")}</ListItemText>
        </MenuItem>
      </Menu>

      <Fade in={showMessage} timeout={300} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            duration={3000}
            type={errorCompetencies.length > 0 ? "error" : "success"}
            onClose={() => setStateOfMessage(false)}
            message={
              <React.Fragment>
                <Typography hidden={errorCompetencies.length === 0}>
                  {errorCompetencies.length === 1
                    ? translate(
                        "settingsViews.competencies.importCompetencies.competencyNotImported",
                        { count: errorCompetencies.length }
                      )
                    : translate(
                        "settingsViews.competencies.importCompetencies.competenciesNotImported",
                        { count: errorCompetencies.length }
                      )}
                </Typography>
                <Typography hidden={!(errorCompetencies.length === 0)}>
                  {cacheRows.importedCompetencies.length === 1
                    ? translate(
                        "settingsViews.competencies.importCompetencies.competencyImported",
                        { count: cacheRows.importedCompetencies.length }
                      )
                    : translate(
                        "settingsViews.competencies.importCompetencies.competenciesImported",
                        { count: cacheRows.importedCompetencies.length }
                      )}
                </Typography>
              </React.Fragment>
            }
            action={
              <React.Fragment>
                <div
                  style={{
                    marginLeft: "auto",
                    marginRight: "0.5rem",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                  hidden={errorCompetencies.length === 0}
                  className='klayo-snackbar_text'
                  onClick={onImportCompetenciesError}
                >
                  {translate("settingsViews.competencies.importCompetencies.viewDetails")}
                </div>
                <BlackIconContainer
                  style={{
                    padding: "0",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                  onClick={() => {
                    // dispatch({ type: 'SET_MESSAGE', payload: false });
                    // Instead of just hiding message, clear selection and potentially show errors
                    setStateOfMessage(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </React.Fragment>
            }
          />
        </div>
      </Fade>

      <AnimatedFooter show={isVisible}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 .35rem"
          }}
        >
          <Typography>
            {selectedRows.length === 1
              ? translate("settingsViews.competencies.importCompetencies.competencySelected", {
                  count: selectedRows.length
                })
              : translate("settingsViews.competencies.importCompetencies.competenciesSelected", {
                  count: selectedRows.length
                })}
          </Typography>
          <Stack direction='row' spacing={2}>
            <Button
              size='md'
              label={translate("common.clear")}
              onClick={onClear}
              sx={{ flexGrow: 1, md: { display: "none" } }}
            />
            <Button
              startIcon={
                <React.Fragment>
                  {importCompetenciesMutation.isPending ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon />
                  )}
                </React.Fragment>
              }
              onClick={onImportCompetencies}
              label={translate("common.import")}
              variant='filled'
              size='md'
              disabled={importCompetenciesMutation.isPending || selectedRows.length === 0}
            />
          </Stack>
        </Box>
      </AnimatedFooter>

      <Fade in={showImportCompetencies} timeout={300} unmountOnExit>
        <div>
          <ImportCompetenciesDialog
            theme={theme}
            competencyId={selectedCompetency}
            onClose={onCloseImportTask}
            open={showImportCompetencies}
            onImport={onImportSingleCompetency}
            isImporting={importCompetenciesMutationSingle.isPending}
          />
        </div>
      </Fade>

      <Fade in={showFilter} timeout={300} unmountOnExit>
        <div>
          <SettingStandardsLibaryFilterCompetencies
            open={showFilter}
            categories={[]}
            theme={theme}
            onClose={onCloseFilter}
            onSave={onSaveFilter}
            isLoading={true}
            initialExpiryFilter={
              competencies.filterRef?.expiry != null
                ? competencies.filterRef?.expiry === true
                  ? YES_NO_OPTIONS.YES
                  : YES_NO_OPTIONS.NO
                : YES_NO_OPTIONS.ALL
            }
            initialTrainingFilter={
              competencies.filterRef?.training != null
                ? competencies.filterRef?.training === true
                  ? YES_NO_OPTIONS.YES
                  : YES_NO_OPTIONS.NO
                : YES_NO_OPTIONS.ALL
            }
            initialSelectedCategories={competencies.filterRef?.categories}
          />
        </div>
      </Fade>

      <Fade in={showStandardsLibraryModalTasks} timeout={300} unmountOnExit>
        <div>
          <SetttingStandardsLibaryListingModal
            theme={theme}
            open={showStandardsLibraryModalTasks}
            columns={[
              { type: StandardLibraryTable.columns.name, label: translate("entities.tasks") },
              {
                type: StandardLibraryTable.columns.listCount,
                field: "taskCompetencies",
                label: translate("entities.tasks")
              }
            ]}
            url={`/StandardLibrary/competencies/${ref.current.id}/tasks`}
            name={ref.current.name}
            extractor={extractorTasks}
            onClose={onCloseCompetenciesListingTasks}
            title={translate("entities.tasks")}
          />
        </div>
      </Fade>

      <Fade in={showStandardsLibraryModalJobs} timeout={300} unmountOnExit>
        <div>
          <SetttingStandardsLibaryListingModal
            theme={theme}
            open={showStandardsLibraryModalJobs}
            columns={[
              { type: StandardLibraryTable.columns.name, label: translate("entities.job") },
              {
                type: StandardLibraryTable.columns.listCount,
                field: "competencies",
                label: translate("tableColumns.competencies"),
                width: 120
              },
              {
                type: StandardLibraryTable.columns.listCount,
                field: "tasks",
                label: translate("entities.tasks")
              },
              {
                type: StandardLibraryTable.columns.listCount,
                field: "trainings",
                label: translate("entities.training")
              }
            ]}
            url={`/StandardLibrary/competencies/${ref.current.id}/jobs`}
            name={ref.current.name}
            extractor={extractorJobs}
            onClose={onCloseCompetenciesListingJobs}
            title={translate("entities.jobs")}
          />
        </div>
      </Fade>

      <ImportCompeteniesErrorDialog
        theme={theme}
        open={showImportCompetenciesError}
        onClose={onCloseImportCompetenciesError}
        competencies={errorCompetencies}
      />

      {isLimitedAccess && (
        <LimitedOverlay
          element={element}
          height={extraCompetencies.length * 56}
          translate={translate}
        />
      )}
    </React.Fragment>
  );
};

export default SettingStandardsLibraryCompetencies;

const BlackIconContainer = styled.div`
  svg {
    path[fill="#1B3DF2"] {
      fill: #000;
    }
  }
`;

const StyledStandardLibraryTable = styled.div`
  .MuiDataGrid-columnHeader:focus-within {
    outline: none !important;
  }
`;
